// components/FAQ.js
import { motion } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import Button from "@/components/Button";
import { SeoInternalLinks } from "@/components/SeoInternalLinks";

export const faqData = {
  partners: [
      {
        question: "How do I set up a referral program?",
        answer: `
          <div class="space-y-4">
            <p>To set up a referral program, first prepare your strategy and use the Afivio integration for a fast and easy launch.</p>
            <p>Log in to your dashboard, go to the Campaigns section, click Create New Campaign, and define the campaign rules and conditions. </p>
            <p>After that, the system will generate unique referral links for your partners. You can also flexibly customize the commission structure, campaign duration, and other settings.</p>
          </div>
        `
      },
      {
        question: "How do I set up an affiliate program?",
        answer: `
          <div class="space-y-4">
            <p>To set up an affiliate program, first define your strategy and use the Afivio integration for a quick and easy launch.</p>
            <p>Log in to your dashboard, go to the Campaigns section, click Create New Campaign, and configure the campaign rules and conditions.</p>
            <p>Once set up, the system will generate unique referral links for your affiliates. You can also customize the commission structure, campaign duration, and other settings.</p>
          </div>
        `
      },
      {
        question: "What analytics are available for tracking campaigns?",
        answer: `
          <div class="space-y-4">
            <p>Afivio’s analytics dashboard provides comprehensive campaign tracking, including real-time performance data such as lead generation, conversion rates, sales performance, and revenue analytics. </p>
            <p>You can also view referral distribution, apply custom date ranges, and export reports in a variety of formats.</p>
          </div>
        `
      },
      {
        question: "How do I pay rewards to partners through the platform?",
        answer: `
          <div class="space-y-4">
            <p>The platform automatically generates a Mass Payout Export report on the 1st of each month for partners whose balance is $10 or more. The report includes the partner’s payment details required to process the payout.</p>
            <p>These details should then be forwarded to the accounting team, and once the payment is completed, it should be marked as paid in the system. Available payout methods include direct bank transfer, PayPal, and cryptocurrency (USDC).</p>
          </div>
        `
      },
      {
        question: "Is there a free trial in Afivio?",
        answer: `
          <div class="space-y-4">
            <p>Yes, we offer a 14-day free trial on all plans.</p>
            <p>During the trial, you get access to all premium features, including unlimited affiliate tracking, the full analytics suite, custom campaign creation, and all payment integrations.</p>
            <p>No credit card is required to start your trial.</p>
          </div>
        `
      },
      // {
      //   question: "How many affiliates can I have on the Spark plan?",
      //   answer: `
      //     <div class="space-y-4">
      //       <p>The Spark plan allows up to 50 active affiliates.</p>
      //       <div class="mt-4">
      //         <p class="font-semibold">This includes:</p>
      //         <ul class="list-disc pl-5 mt-2">
      //           <li>Individual promoters</li>
      //           <li>Content creators</li>
      //           <li>Business partners</li>
      //         </ul>
      //       </div>
      //       <p class="mt-4">You can upgrade to higher tiers:</p>
      //       <ul class="list-disc pl-5 mt-2">
      //         <li>Boost: 200 affiliates</li>
      //         <li>Rocket: 1000 affiliates</li>
      //         <li>Galaxy: Unlimited affiliates</li>
      //       </ul>
      //     </div>
      //   `
      // },
      {
        question: "What reward systems are available for partners in Afivio?",
        answer: `
          <div class="space-y-4">
            <p>Afivio operates on a performance-based payment model and supports reward structures in CPA, CPS, and CPL models. You can pay partners a commission for a completed sale, for a generated lead, or use more flexible reward models.</p>
            <p>Available options include percentage-based commissions, fixed amount payouts, hybrid models (base fee + percentage), tiered rewards based on performance, milestone bonuses, and custom conditions for different products or services.</p>
          </div>
        `
      },
      {
        question: "What integration methods does Afivio support for websites?",
        answer: `
          <div class="space-y-4">
            <p>Afivio supports integration with virtually any website through Cookie Script, Stripe, or API. </p>
            <p>Detailed setup instructions are available in the learning panel after you log in to your account, and our support team is always ready to help if you have any questions.</p>
          </div>
        `
      },
      {
        question: "Does Afivio take a percentage of sales like other popular platforms?",
        answer: `
          <div class="space-y-4">
            <p>No. Afivio does not charge any commission on sales.</p>
            <p>Pricing plans start at $39, and that is the final price with no hidden fees or additional sales commissions.</p>
          </div>
        `
      },
      {
        question: "How quickly can I launch an affiliate program?",
        answer: `
          <div class="space-y-4">
            <p>If you have already defined your affiliate marketing strategy, the technical setup can take as little as 1 hour.</p>
            <p>After the setup is complete, it is important to test how the system works and share information about your program through your communication channels to start attracting partners.</p>
          </div>
        `
      },
      {
        question: "Do I need a bank card to get started?",
        answer: `
          <div class="space-y-4">
            <p>No, you do not need a bank card to get started.</p>
          </div>
        `
      },
      {
        question: "How can I invite partners to the program?",
        answer: `
          <div class="space-y-4">
            <p>You can invite partners by sending them a personal link or inviting them via email. Mass invitations are also available — simply upload your contact database into the system.</p>
            <p>If the campaign is public, partners can also discover your program on the Afivio website, as well as on your website or through your communication channels.</p>
          </div>
        `
      },
      {
        question: "Can I create different terms for different partners?",
        answer: `
          <div class="space-y-4">
            <p>Yes. You can run multiple campaigns at the same time with different terms for different partners or partner groups.</p>
          </div>
        `
      },
      {
        question: "Can I run multiple campaigns at the same time?",
        answer: `
          <div class="space-y-4">
            <p>Yes, Afivio allows you to run multiple campaigns simultaneously.</p>
          </div>
        `
      },
      {
        question: "Can I track the performance of each partner individually?",
        answer: `
          <div class="space-y-4">
            <p>Yes. You can view individual statistics for each partner and track their performance separately.</p>
          </div>
        `
      },
      {
        question: "Is support available during setup?",
        answer: `
          <div class="space-y-4">
            <p>Yes. The Boost, Rocket, and Galaxy plans include personal onboarding with a manager to help you during setup.</p>
          </div>
        `
      },
      {
        question: "Are there any learning materials or a knowledge base?",
        answer: `
          <div class="space-y-4">
            <p>Yes. After logging into your account, you will have access to step-by-step guidance, setup instructions, and educational materials to help you configure your campaign independently. We also regularly publish useful educational content on our blog.</p>
          </div>
        `
      },
      {
        question: "Is there a built-in messenger for communicating with partners?",
        answer: `
          <div class="space-y-4">
            <p>Yes, the Afivio platform includes a built-in messenger for communication with partners.</p>
          </div>
        `
      },
      {
        question: "How can I contact support?",
        answer: `
          <div class="space-y-4">
            <p>You can contact our support team at contact@afivio.com.</p>
          </div>
        `
      }
  ],
  affiliates: [
      {
        question: "How do I join as an affiliate?",
        answer: `
          <div class="space-y-4">
            <p>Joining as an affiliate is simple:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Create your account</li>
              <li>Complete your profile</li>
              <li>Browse available campaigns</li>
              <li>Apply to programs matching your audience</li>
              <li>Get approved by the partner</li>
              <li>Start promoting</li>
            </ul>
            <div class="mt-4">
              <p class="font-semibold">You'll need:</p>
              <ul class="list-disc pl-5 mt-2">
                <li>Valid email</li>
                <li>Payment information</li>
                <li>Basic profile details</li>
              </ul>
            </div>
          </div>
        `
      },
      {
        question: "How do I earn rewards?",
        answer: `
          <div class="space-y-4">
            <p>Earn rewards through multiple channels:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Direct sales via your referral link</li>
              <li>Conversions from your referral code</li>
              <li>First-time customer purchases</li>
              <li>Recurring revenue (if applicable)</li>
              <li>Special bonuses for milestones</li>
            </ul>
            <p class="mt-4">Each sale is tracked automatically and credited to your account.</p>
          </div>
        `
      },
      {
        question: "When are payments sent?",
        answer: `
          <div class="space-y-4">
            <p>Payment schedule:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Monthly payouts on the 1st</li>
              <li>Minimum $10 balance required</li>
              <li>Multiple payment options: Stripe, PayPal, or cryptocurrency</li>
              <li>2-3 business days processing time</li>
              <li>Automatic payment processing</li>
              <li>Payment history available in dashboard</li>
            </ul>
          </div>
        `
      },
      {
        question: "Is there a minimum payout?",
        answer: `
          <div class="space-y-4">
            <p>Yes, there is a $10 minimum payout threshold.</p>
            <div class="mt-4">
              <p class="font-semibold">This applies to:</p>
              <ul class="list-disc pl-5 mt-2">
                <li>All payment methods</li>
                <li>Monthly payout cycles</li>
                <li>Individual campaign earnings</li>
              </ul>
            </div>
            <p class="mt-4">Unpaid balances roll over to the next month. You can track your current balance in real-time through your dashboard.</p>
          </div>
        `
      },
      {
        question: "Can I promote multiple campaigns?",
        answer: `
          <div class="space-y-4">
            <p>Yes, you can join and promote unlimited campaigns:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Different products/services</li>
              <li>Various commission rates</li>
              <li>Multiple niches</li>
              <li>Different partners</li>
            </ul>
            <div class="mt-4">
              <p class="font-semibold">Each campaign has:</p>
              <ul class="list-disc pl-5 mt-2">
                <li>Unique tracking links</li>
                <li>Separate performance metrics</li>
                <li>Individual commission structures</li>
              </ul>
            </div>
          </div>
        `
      },
      {
        question: "How do I track my earnings?",
        answer: `
          <div class="space-y-4">
            <p>Track earnings through your affiliate dashboard:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Real-time earnings updates</li>
              <li>Detailed transaction history</li>
              <li>Performance analytics</li>
              <li>Conversion tracking</li>
              <li>Click statistics</li>
              <li>Exportable reports</li>
              <li>Custom date range filtering</li>
            </ul>
          </div>
        `
      },
      {
        question: "Are there fees?",
        answer: `
          <div class="space-y-4">
            <p>No fees for affiliates:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Zero platform fees</li>
              <li>No registration costs</li>
              <li>Free access to all tools</li>
              <li>No hidden charges</li>
            </ul>
            <p class="mt-4">The only fees you might encounter are: Standard payment processor fees (if applicable) and bank transfer fees (if using bank transfers).</p>
          </div>
        `
      },
      {
        question: "What payment methods are supported?",
        answer: `
          <div class="space-y-4">
            <p>Multiple payment options available:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Stripe (direct bank transfer)</li>
              <li>PayPal (personal/business accounts)</li>
              <li>Cryptocurrency (BTC, ETH, USDT)</li>
            </ul>
            <div class="mt-4">
              <p class="font-semibold">Each method offers:</p>
              <ul class="list-disc pl-5 mt-2">
                <li>Secure transactions</li>
                <li>Fast processing</li>
                <li>Detailed payment history</li>
              </ul>
            </div>
          </div>
        `
      },
      {
        question: "How do referral links work?",
        answer: `
          <div class="space-y-4">
            <p>Referral links are unique tracking URLs:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Automatically generated for each campaign</li>
              <li>Track clicks and conversions</li>
              <li>Attribute sales to your account</li>
              <li>Provide real-time analytics</li>
              <li>Can be customized with parameters</li>
              <li>Work across all devices and platforms</li>
            </ul>
          </div>
        `
      },
      {
        question: "Can I customize my links?",
        answer: `
          <div class="space-y-4">
            <p>Yes, you can customize your links:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Add tracking parameters</li>
              <li>Create branded short links</li>
              <li>Set up subdomain tracking</li>
              <li>Add UTM parameters</li>
              <li>Create platform-specific links</li>
            </ul>
            <p class="mt-4">All customizations maintain tracking functionality.</p>
          </div>
        `
      },
      {
        question: "Do I need a website?",
        answer: `
          <div class="space-y-4">
            <p>No website required. You can promote through:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Social media platforms</li>
              <li>Email lists</li>
              <li>YouTube channels</li>
              <li>Blog posts</li>
              <li>Online communities</li>
              <li>Personal networks</li>
            </ul>
            <div class="mt-4">
              <p class="font-semibold">Focus on:</p>
              <ul class="list-disc pl-5 mt-2">
                <li>Building trust</li>
                <li>Providing value</li>
                <li>Following platform guidelines</li>
              </ul>
            </div>
          </div>
        `
      },
      {
        question: "Are there geographic restrictions?",
        answer: `
          <div class="space-y-4">
            <p>Restrictions vary by campaign:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Check individual campaign terms</li>
              <li>Some programs are region-specific</li>
              <li>Payment methods may vary by country</li>
              <li>Commission rates might differ by region</li>
            </ul>
            <p class="mt-4">Always review campaign details before applying.</p>
          </div>
        `
      },
      {
        question: "Can I promote on multiple platforms?",
        answer: `
          <div class="space-y-4">
            <p>Yes, promote across multiple platforms:</p>
            <ul class="list-disc pl-5 space-y-2">
              <li>Social media (Instagram, Facebook, Twitter)</li>
              <li>YouTube channels</li>
              <li>Blogs and websites</li>
              <li>Email marketing</li>
              <li>Online forums</li>
              <li>Personal networks</li>
            </ul>
            <div class="mt-4">
              <p class="font-semibold">Each platform can have:</p>
              <ul class="list-disc pl-5 mt-2">
                <li>Unique tracking links</li>
                <li>Platform-specific content</li>
                <li>Different promotion strategies</li>
              </ul>
            </div>
          </div>
        `
      }
  ]
};

export const FAQ = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-5xl font-bold text-center mb-16 text-gray-900"
        >
          Frequently Asked Questions
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Для партнерів */}
          <div className="space-y-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">For partners</h3>
            {faqData.partners.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer">
                    <span className="text-lg font-medium text-gray-900">{item.question}</span>
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 transform group-open:rotate-180 transition-transform"/>
                  </summary>
                  <div 
                    className="mt-4 text-gray-600 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </details>
              </motion.div>
            ))}
          </div>

          {/* Для афілієйтів */}
          <div className="space-y-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">For affiliates</h3>
            {faqData.affiliates.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer">
                    <span className="text-lg font-medium text-gray-900">{item.question}</span>
                    <ChevronDownIcon className="w-5 h-5 text-gray-500 transform group-open:rotate-180 transition-transform"/>
                  </summary>
                  <div 
                    className="mt-4 text-gray-600 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: item.answer }}
                  />
                </details>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Блок */}
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="mt-20 text-center bg-blue-600 p-8 rounded-2xl shadow-xl"
        >
          <h3 className="text-2xl font-bold text-white mb-4">Any other questions?</h3>
          <p className="mx-auto mb-6 max-w-2xl text-blue-100">
            Review pricing, see how setup works, or contact support if you need help choosing the right integration.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button gray href="/pricing">
              View Pricing
            </Button>
            <Button white href="/how-it-works">
              See How It Works
            </Button>
            <Button secondary href="/contact">
              Contact Support
            </Button>
          </div>
        </motion.div>
      </div>
      <SeoInternalLinks className="mt-12" heading="Explore related Afivio pages" />
    </section>
  );
};
